import { coerceGradeValue } from "../form/runtimeModel";
import { isMaintenanceCompleted, maintenanceEventId } from "../lib/maintenanceEdit";
import type { HuelleAsset } from "./planModel";

/**
 * Einheitlicher Wartungsstatus eines Objekts innerhalb der aktuellen Wartung.
 *
 * - open:        noch nichts passiert
 * - in_progress: Formular gestartet / Zwischenstand gespeichert
 * - ok:          abgeschlossen, in Ordnung
 * - defect:      abgeschlossen, Mangel festgestellt
 * - follow_up:   abgeschlossen, Nacharbeit nötig
 * - closed:      abgeschlossen (ohne Ergebnisdetail)
 */
export type AssetStatusKey = "open" | "in_progress" | "ok" | "defect" | "follow_up" | "closed";

export type AssetStatusFilter = "all" | "open" | "in_progress" | "done" | "issues";

export interface AssetStatusView {
  key: AssetStatusKey;
  label: string;
  /** Kurzbeschreibung (z. B. für Tooltips). */
  hint: string;
  /** CSS-Klasse (siehe huelleDetail.css). */
  className: string;
  isDone: boolean;
  hasIssue: boolean;
}

const VIEWS: Record<AssetStatusKey, Omit<AssetStatusView, "key">> = {
  open: {
    label: "Offen",
    hint: "Für dieses Objekt wurde noch keine Wartung gestartet.",
    className: "huelle-status huelle-status--open",
    isDone: false,
    hasIssue: false,
  },
  in_progress: {
    label: "In Bearbeitung",
    hint: "Das Wartungsformular wurde begonnen, aber noch nicht abgeschlossen.",
    className: "huelle-status huelle-status--progress",
    isDone: false,
    hasIssue: false,
  },
  ok: {
    label: "In Ordnung",
    hint: "Wartung abgeschlossen, keine Mängel.",
    className: "huelle-status huelle-status--ok",
    isDone: true,
    hasIssue: false,
  },
  defect: {
    label: "Mangel",
    hint: "Wartung abgeschlossen, Mangel festgestellt.",
    className: "huelle-status huelle-status--defect",
    isDone: true,
    hasIssue: true,
  },
  follow_up: {
    label: "Nacharbeit",
    hint: "Wartung abgeschlossen, Nacharbeit erforderlich.",
    className: "huelle-status huelle-status--follow-up",
    isDone: true,
    hasIssue: true,
  },
  closed: {
    label: "Abgeschlossen",
    hint: "Wartung abgeschlossen.",
    className: "huelle-status huelle-status--ok",
    isDone: true,
    hasIssue: false,
  },
};

export function resolveAssetStatus(asset: HuelleAsset | null | undefined): AssetStatusView {
  let key: AssetStatusKey = "open";
  if (asset) {
    if (isMaintenanceCompleted(asset)) {
      const result = String(asset.current_maintenance_result_status ?? asset.result_status ?? "")
        .trim()
        .toLowerCase();
      if (result === "ok") key = "ok";
      else if (result === "defect_found") key = "defect";
      else if (result === "follow_up_required") key = "follow_up";
      else key = "closed";
    } else if (
      asset.current_maintenance_started === true ||
      asset.current_maintenance_has_form_data === true
    ) {
      key = "in_progress";
    }
  }
  return { key, ...VIEWS[key] };
}

/** Note der Wartung in dieser Kampagne. Offen und in Bearbeitung haben keine Note. */
export function resolveAssetGrade(asset: HuelleAsset | null | undefined): number | null {
  if (!asset || !isMaintenanceCompleted(asset)) return null;
  const eventId = maintenanceEventId(asset);
  const recent = Array.isArray(asset.recent_maintenances)
    ? (asset.recent_maintenances as { maintenance_event_id?: number | null; grade_final?: number | null }[])
    : [];
  const match =
    eventId != null
      ? recent.find((entry) => Number(entry?.maintenance_event_id) === eventId)
      : null;
  const fromHistory = match ? coerceGradeValue(match.grade_final) : null;
  if (fromHistory != null) return fromHistory;
  return coerceGradeValue(asset.grade_final ?? asset.current_maintenance_grade_final);
}

export function assetMatchesStatusFilter(asset: HuelleAsset, filter: AssetStatusFilter): boolean {
  if (filter === "all") return true;
  const status = resolveAssetStatus(asset);
  switch (filter) {
    case "open":
      return status.key === "open";
    case "in_progress":
      return status.key === "in_progress";
    case "done":
      return status.isDone;
    case "issues":
      return status.hasIssue;
    default:
      return true;
  }
}

export interface AssetStatusCounts {
  total: number;
  open: number;
  inProgress: number;
  done: number;
  issues: number;
}

export function countAssetStatuses(assets: HuelleAsset[]): AssetStatusCounts {
  const counts: AssetStatusCounts = { total: 0, open: 0, inProgress: 0, done: 0, issues: 0 };
  for (const asset of assets) {
    counts.total += 1;
    const status = resolveAssetStatus(asset);
    if (status.key === "open") counts.open += 1;
    else if (status.key === "in_progress") counts.inProgress += 1;
    if (status.isDone) counts.done += 1;
    if (status.hasIssue) counts.issues += 1;
  }
  return counts;
}
